import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { buildDeleteConfig } from '../../utils/delete-confirmation';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { FormulaFormHelperService } from '../../services/formula-load-save-form-helper.service';
import { FormulaFormProvider } from '../../services/formula-model-dialog.service';
import { TestModeService } from '../../services/test-mode.service';
import { BusinessRuleType, EntityDefinition } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction } from '../../types/entity-form-result.interface';
import { Formula, FormulaParameter, FormulaParameters } from '../../types/formula.interface';
import { ISODateTime, MeasureFormula, MeasureFormulaStatus } from '../../types/measure-formula.interface';
import { Measure } from '../../types/measure.interface';
import { Unit } from '../../types/unit.interface';

interface MeasureFormulaFormControls {
  name: FormControl<string>;
  startTs: FormControl<string | null>;
  expression: FormControl<string>;
  parameters: FormControl<FormulaParameters>;
  comment: FormControl<string>;
}

interface MeasureFormulaDialogAdditionalData {
  measureId?: number;
  units?: Unit[];
  measures?: Measure[];
  allowedParameterTypes?: readonly string[];
  historicalFunctionsAvailable?: boolean;
  formulas?: MeasureFormula[];
  selectedMeasureFormula?: MeasureFormula;
}

interface MeasureFormulaDialogData extends Omit<EntityDialogData<MeasureFormula>, 'additionalData'> {
  additionalData?: MeasureFormulaDialogAdditionalData;
}
import { CesDeleteButtonComponent } from '../ui/ces-buttons';
import { CesFormulaModelButtonsComponent } from '../ui/ces-buttons/ces-formula-model-buttons.component';
import { CesDialogFooterComponent } from '../ui/dialog-footer/dialog-footer.component';
import { CesTextFieldComponent } from '../ui/ces-text-field/ces-text-field.component';
import { FormulaHelpModalComponent } from '../ui/formula-help-modal/formula-help-modal.component';
import { CesDatepickerComponent } from '../ui/inputs/datepicker/datepicker.component';
import { EditorComponent } from '../ui/inputs/editor/editor.component';
import { EditorVariable } from '../ui/inputs/editor/editor.types';
import { ParameterMode } from '../ui/inputs/parameters-input/parameter-types';
import { ParametersComponent } from '../ui/inputs/parameters-input/parameters.component';

import { CesTooltipAutoDismissDirective } from '../../directives/ces-tooltip-auto-dismiss.directive';
@Component({
  selector: 'app-measure-formula-form',
  standalone: true,
  imports: [
    CesTooltipAutoDismissDirective,
    CesDatepickerComponent,
    CesDeleteButtonComponent,
    CesDialogFooterComponent,
    CesFormulaModelButtonsComponent,
    CesTextFieldComponent,
    DatePipe,
    EditorComponent,
    FormulaHelpModalComponent,
    FormsModule,
    InputTextModule,
    ParametersComponent,
    ReactiveFormsModule,
    TextareaModule,
    TooltipModule,
    TranslocoModule,
  ],
  providers: [DialogService],
  templateUrl: './measure-formula-form.component.html',
})
export class MeasureFormulaFormComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() entity?: MeasureFormula;
  @Input() measureId!: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() units: Unit[] = [];
  @Input() measures: Measure[] = [];
  @Input() allowedParameterTypes?: readonly string[];
  /** CES-604 — false pour les formules des mesures automatiques. */
  @Input() historicalFunctionsAvailable = true;

  @Output() formSubmit = new EventEmitter<MeasureFormula>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<MeasureFormula>();

  @ViewChild('formContainer') formContainer!: ElementRef<HTMLElement>;
  @ViewChild('expressionEditor') expressionEditor!: EditorComponent;

  readonly ParameterMode = ParameterMode;

  form!: FormGroup<MeasureFormulaFormControls>;
  parametersControl!: FormControl<FormulaParameters>;
  editorVariables: EditorVariable[] = [];
  submitting = false;
  errorMessage = '';
  showHelpDialog = false;

  private readonly fb = inject(FormBuilder);
  protected readonly config = inject<DynamicDialogConfig<MeasureFormulaDialogData> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<MeasureFormulaFormComponent> | null>(DynamicDialogRef, {
    optional: true,
  });

  protected readonly t = inject(TestModeService);
  private readonly destroy$ = new Subject<void>();

  private directDeleteCallback?: (entity: MeasureFormula) => void;
  private entityDefinition?: EntityDefinition<MeasureFormula>;

  private readonly helper = inject(FormulaFormHelperService);
  private readonly translateService = inject(TranslocoService);
  private formulaProvider!: FormulaFormProvider;

  ngOnInit(): void {
    const data = this.config?.data;
    if (data) {
      const additional = data.additionalData ?? {};
      if (data.entity !== undefined) this.entity = data.entity;
      this.measureId = additional.measureId ?? this.measureId;
      this.mode = data.mode === 'edit' ? 'edit' : 'create';
      this.units = additional.units ?? [];
      this.measures = additional.measures ?? [];
      if (additional.allowedParameterTypes !== undefined) this.allowedParameterTypes = additional.allowedParameterTypes;
      if (additional.historicalFunctionsAvailable !== undefined)
        this.historicalFunctionsAvailable = additional.historicalFunctionsAvailable;
      if (data.onDelete !== undefined) this.directDeleteCallback = data.onDelete;
      if (data.entityDefinition !== undefined) this.entityDefinition = data.entityDefinition;
    }

    this.initForm();

    const providedEntity = this.entity ?? this.config?.data?.additionalData?.selectedMeasureFormula;
    if (providedEntity?.formula) {
      this.patchFormWithEntity(providedEntity);
    }

    this.formulaProvider = this.helper.createProvider(
      this.form,
      {
        expressionField: 'expression',
        parametersField: 'parameters',
        nameField: 'name',
        commentField: 'comment',
      },
      params => (this.editorVariables = this.helper.parametersToEditorVariables(params)),
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.formContainer) {
        this.formContainer.nativeElement.focus();
      }

      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && activeElement.tagName !== 'BODY') {
        activeElement.blur();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private atLeastOneParameterValidator(control: AbstractControl): ValidationErrors | null {
    const value: unknown = control.value;
    return Array.isArray(value) && value.length > 0 ? null : { noParameters: true };
  }

  private isValidDateValidator(control: AbstractControl): ValidationErrors | null {
    const date: unknown = control.value;
    if (!date) return { required: true };

    if (date instanceof Date) {
      return isNaN(date.getTime()) ? { invalidDate: true } : null;
    }

    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      return isNaN(parsedDate.getTime()) ? { invalidDate: true } : null;
    }

    return { invalidDate: true };
  }

  private computeDefaultStartTs(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private initForm(): void {
    this.parametersControl = this.fb.nonNullable.control([] as FormulaParameters, [
      Validators.required,
      this.atLeastOneParameterValidator,
    ]);

    this.form = new FormGroup<MeasureFormulaFormControls>({
      name: this.fb.nonNullable.control(''),
      startTs: this.fb.control<string | null>(this.computeDefaultStartTs().toISOString(), [
        Validators.required,
        this.isValidDateValidator,
      ]),
      expression: this.fb.nonNullable.control('', [Validators.required]),
      parameters: this.parametersControl,
      comment: this.fb.nonNullable.control(''),
    });

    // La date de formule est figée à l'édition : l'API rejette toute modification
    // de startTs/endTs sur un contenu existant (MEASURE_FORMULA_DATES_LOCKED).
    if (this.mode === 'edit') {
      this.form.get('startTs')?.disable();
    }

    this.form.get('startTs')?.markAsTouched();
    this.form.get('startTs')?.updateValueAndValidity();

    this.parametersControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(parameters => {
      if (parameters) {
        this.updateEditorVariables(parameters);
      }
    });
  }

  private patchFormWithEntity(entity: MeasureFormula): void {
    if (!entity.formula) return;

    const parameters = entity.formula.parameters || [];
    this.updateEditorVariables(parameters);

    this.form.patchValue({
      name: entity.formula.name ?? '',
      expression: entity.formula.expression,
      parameters: parameters,
      comment: entity.formula.comment ?? '',
    });

    if (this.mode === 'edit') {
      this.form.patchValue({ startTs: entity.startTs ?? null });
    }
  }

  updateEditorVariables(parameters: FormulaParameter[]): void {
    this.editorVariables = parameters.map(param => ({
      name: param.symbol,
      value: param.name,
      description: param.name,
    }));
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field?.errors) return '';

    if (field.errors['required']) return this.translateService.translate('validation.required');
    const minLengthError = field.errors['minlength'] as { requiredLength?: number } | undefined;
    if (minLengthError) return this.translateService.translate('validation.minLength', { min: minLengthError.requiredLength });
    const maxLengthError = field.errors['maxlength'] as { requiredLength?: number } | undefined;
    if (maxLengthError) return this.translateService.translate('validation.maxLength', { max: maxLengthError.requiredLength });
    if (field.errors['invalidDate']) return this.translateService.translate('validation.invalidDate');
    if (field.errors['noParameters']) return this.translateService.translate('validation.atLeastOneParameter');

    return this.translateService.translate('validation.invalidField');
  }

  onExpressionChange(_expression: string): void {}

  onParametersChange(parameters: FormulaParameter[]): void {
    this.editorVariables = this.helper.parametersToEditorVariables(parameters);
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      this.errorMessage = this.translateService.translate('validation.formHasErrors');
      return;
    }

    const formValue = this.form.getRawValue();

    if (!formValue.parameters || formValue.parameters.length === 0) {
      this.errorMessage = this.translateService.translate('validation.atLeastOneParameter');
      return;
    }

    if (!formValue.expression || formValue.expression.trim() === '') {
      this.errorMessage = this.translateService.translate('validation.expressionRequired');
      return;
    }

    if (!formValue.startTs) {
      this.errorMessage = this.translateService.translate('validation.startDateRequired');
      return;
    }

    this.submitting = true;
    this.errorMessage = '';

    const formula: Formula = {
      id: this.entity?.formula?.id ?? 0,
      name: formValue.name,
      expression: formValue.expression,
      comment: formValue.comment,
      parameters: formValue.parameters,
    };

    // La date de formule est figée à l'édition (MEASURE_FORMULA_DATES_LOCKED) : le PUT ne
    // transporte que le contenu. La création reste datée : le POST attend {startTs, formula}.
    if (this.mode === 'edit') {
      const editPayload = { id: this.entity?.id ?? 0, formula };
      if (this.dialogRef) {
        this.dialogRef.close(editPayload);
      } else {
        this.formSubmit.emit(editPayload as unknown as MeasureFormula);
      }
      this.submitting = false;
      return;
    }

    const startTs: ISODateTime = formValue.startTs;

    const measureFormula: MeasureFormula = {
      id: this.entity?.id ?? 0,
      measureId: this.measureId,
      formulaId: this.entity?.formulaId ?? 0,
      startTs,
      formula,
      name: formValue.name,
      status: this.entity?.status ?? MeasureFormulaStatus.ACTIVE,
      // A newly painted segment carries no recorded value yet — the server response
      // (re-fetched right after creation) is what the timeline actually renders.
      hasValues: false,
    };

    if (this.dialogRef) {
      this.dialogRef.close(measureFormula);
    } else {
      this.formSubmit.emit(measureFormula);
    }

    this.submitting = false;
  }

  onCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  onDelete(): void {
    if (!this.entity) return;

    if (this.directDeleteCallback) {
      this.directDeleteCallback(this.entity);
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    } else if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<MeasureFormula> = { __action: 'delete', entity: this.entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(this.entity);
    }
  }

  getDeleteConfirmationConfig() {
    return buildDeleteConfig('entities.measureFormula.messages.deleteConfirm', this.entity?.name ?? '');
  }

  private deleteValidationRules() {
    return (this.entityDefinition?.validationRules ?? []).filter(rule => rule.type === BusinessRuleType.DELETE_VALIDATION);
  }

  // Mirrors GenericEntityFormComponent.canDeleteEntity(): evaluates the entity definition's
  // DELETE_VALIDATION rules (e.g. blocking a valued formula segment) against the edited
  // entity — this bespoke form skips the generic dialog, so it re-applies the same catalog
  // rule directly rather than inventing an ad-hoc flag.
  canDeleteFormula(): boolean {
    const entity = this.entity;
    if (!entity) return false;
    return this.deleteValidationRules().every(rule => rule.rule(entity));
  }

  deleteBlockedTooltip(): string {
    const entity = this.entity;
    if (!entity) return '';
    return this.deleteValidationRules()
      .filter(rule => !rule.rule(entity))
      .map(rule => (rule.errorMessageKey ? this.translateService.translate(rule.errorMessageKey) : (rule.errorMessage ?? '')))
      .join('\n');
  }

  markDatePickerAsTouchedAsync(): void {
    const startTsControl = this.form.get('startTs');
    if (startTsControl) {
      setTimeout(() => {
        this.form.get('startTs')?.markAsTouched();
      }, 500);
    }
  }

  openLoadFormulaModelDialog(): void {
    this.helper.openLoadDialog(this.formulaProvider, {
      measureId: this.measureId,
      units: this.units,
      measures: this.measures,
    });
  }

  openSaveModelTemplateDialog(): void {
    this.helper.openSaveDialog(this.formulaProvider, {
      measureId: this.measureId,
      units: this.units,
      measures: this.measures,
    });
  }

  showFormulaHelp(): void {
    this.showHelpDialog = true;
  }

  onFunctionInsert(snippet: string): void {
    if (this.expressionEditor) {
      this.expressionEditor.insertText(snippet);
    }
  }
}
